import { GenerateUploadUrlRequest } from '../../../models/image/requests/generate-upload-url-request';
import { UploadFilePath } from '../../../models/enum/dto/upload-file.path';

/**
 * Metadata keys the backend understands for each media class. Anything else is rejected as unsupported.
 * Keep these keys in sync with the backend upload contract.
 */
export const supportedUploadMetadataByMediaClass: ReadonlyMap<UploadFilePath, ReadonlySet<string>> = new Map<
  UploadFilePath,
  ReadonlySet<string>
>([[UploadFilePath.AssetLibraryPath, new Set(['CompanyId', 'Tags', 'AssetLibraryType', 'ParentFolderId'])]]);

/**
 * Metadata keys that are required (non-empty) for each media class. Missing/blank values yield a validation
 * error before the URL is generated.
 */
export const requiredUploadMetadataByMediaClass: ReadonlyMap<UploadFilePath, ReadonlySet<string>> = new Map<
  UploadFilePath,
  ReadonlySet<string>
>([[UploadFilePath.AssetLibraryPath, new Set(['CompanyId'])]]);

/** Return `null` when the request is valid, otherwise a `.`-joined error message ready for a throwError. */
export function preflightGenerateUploadUrlValidation(req: GenerateUploadUrlRequest): string | null {
  const errors: string[] = [];
  if (!req.fileName?.trim()) {
    errors.push('File name is required');
  }
  if (!req.mediaClass) {
    errors.push('Media class is required');
  }
  // I'm assuming this check is not required if uploadFilePath is always AssetLibraryPath
  if (req.mediaClass !== UploadFilePath.AssetLibraryPath && !req.mediaId?.trim()) {
    errors.push('Media ID is required');
  }
  if (!req.mediaType?.trim()) {
    errors.push('Media type is required');
  }
  if (!req.metadata) {
    errors.push('Metadata is required');
  } else {
    const metadataErrors = preflightMetadataValidation(req.mediaClass, req.metadata);
    if (metadataErrors.length) {
      errors.push(...metadataErrors);
    }
  }
  return errors.length ? errors.join('. ') : null;
}

/** Verify every metadata entry, including presence of required keys and well-formed Tags. */
export function preflightMetadataValidation(mediaClass: UploadFilePath, metadata: Map<string, string>): string[] {
  const errors: string[] = [];
  const supportedMetadata = supportedUploadMetadataByMediaClass.get(mediaClass);
  const requiredMetadata = requiredUploadMetadataByMediaClass.get(mediaClass);
  if (!supportedMetadata && !requiredMetadata) {
    return errors;
  }
  metadata.forEach((value, key) => {
    if (!supportedMetadata?.has(key)) {
      errors.push(`Metadata '${key}' is not supported for media class '${mediaClass}'`);
    }
    if (typeof value !== 'string') {
      errors.push(`Metadata '${key}' must be a string`);
    }
  });
  requiredMetadata?.forEach(key => {
    if (!metadata.get(key)?.trim()) {
      errors.push(`Metadata '${key}' is required for media class '${mediaClass}'`);
    }
  });
  const tags = metadata.get('Tags');
  if (tags && tags.split(',').some(tag => !tag?.trim())) {
    errors.push(`Metadata 'Tags' must be a comma-separated string with non-empty values`);
  }
  return errors;
}
